import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { LotteryBallGroup } from '../components/LotteryBall';
import DisclaimerBanner from '../components/DisclaimerBanner';
import { FrequencyChart, DecadeDistributionChart, TrendChart } from '../components/lottery-history/HistoryCharts';
import { toast } from 'sonner';
import { 
  Flame, 
  Snowflake, 
  TrendingUp,
  ArrowLeft,
  BarChart3,
  Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';

const LotteryHistory = () => {
  const { lotteryId } = useParams();
  const { token } = useAuth();
  const [lottery, setLottery] = useState(null);
  const [results, setResults] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [lotteryRes, resultsRes, analysisRes] = await Promise.all([
        axios.get(`${API}/lotteries/${lotteryId}`),
        axios.get(`${API}/results?lottery_id=${lotteryId}&limit=100`),
        axios.get(`${API}/analysis/${lotteryId}?days=${period}`)
      ]);
      
      setLottery(lotteryRes.data);
      setResults(resultsRes.data);
      setAnalysis(analysisRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [lotteryId, period]);

  useEffect(() => {
    if (lotteryId) {
      fetchData();
    }
  }, [lotteryId, period, fetchData]);

  const frequencyChartData = useMemo(() => {
    if (!analysis?.frequency) return [];
    const data = [];
    for (let i = 0; i <= 99; i++) {
      data.push({ number: i.toString().padStart(2, '0'), count: analysis.frequency[i.toString()] || 0 });
    }
    return data;
  }, [analysis]);

  const top20Numbers = useMemo(() => {
    if (!analysis?.frequency) return [];
    return Object.entries(analysis.frequency)
      .map(([num, count]) => ({ number: num.padStart(2, '0'), count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
  }, [analysis]);

  const numberTrendData = useMemo(() => {
    if (!results.length) return [];
    const last30 = results.slice(0, 30).reverse();
    return last30.map((r) => ({
      date: r.draw_date.slice(5),
      num1: r.numbers[0] || 0,
      num2: r.numbers[1] || 0,
      num3: r.numbers[2] || 0,
      avg: r.numbers.reduce((a, b) => a + b, 0) / r.numbers.length
    }));
  }, [results]);

  const decadeDistribution = useMemo(() => {
    if (!analysis?.frequency) return [];
    const decades = [
      { name: '00-09', range: [0, 9] }, { name: '10-19', range: [10, 19] },
      { name: '20-29', range: [20, 29] }, { name: '30-39', range: [30, 39] },
      { name: '40-49', range: [40, 49] }, { name: '50-59', range: [50, 59] },
      { name: '60-69', range: [60, 69] }, { name: '70-79', range: [70, 79] },
      { name: '80-89', range: [80, 89] }, { name: '90-99', range: [90, 99] },
    ];
    return decades.map(d => {
      let count = 0;
      for (let i = d.range[0]; i <= d.range[1]; i++) {
        count += analysis.frequency[i.toString()] || 0;
      }
      return { name: d.name, value: count };
    });
  }, [analysis]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!lottery) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <p className="text-slate-400">Lotería no encontrada</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-20">
      <Navbar />
      
      <main className="pt-20 px-4 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          <Link to="/dashboard">
            <Button variant="ghost" className="text-slate-400 hover:text-white">
              <ArrowLeft size={18} className="mr-2" />
              Volver
            </Button>
          </Link>
          
          <div className="flex items-center gap-4">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: lottery.color }}
            >
              <BarChart3 size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="lottery-history-title">
                {lottery.name}
              </h1>
              <p className="text-slate-400">{lottery.company} • {lottery.draw_time}</p>
            </div>
          </div>
          
          <div className="md:ml-auto">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[140px] bg-slate-900 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="7" className="text-white hover:bg-slate-800">7 días</SelectItem>
                <SelectItem value="30" className="text-white hover:bg-slate-800">30 días</SelectItem>
                <SelectItem value="60" className="text-white hover:bg-slate-800">60 días</SelectItem>
                <SelectItem value="90" className="text-white hover:bg-slate-800">90 días</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="glass-card">
            <CardContent className="py-4">
              <p className="text-slate-400 text-sm">Sorteos Analizados</p>
              <p className="text-2xl font-bold text-white font-mono">{analysis?.total_draws_analyzed || 0}</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-pink-500/30">
            <CardContent className="py-4">
              <p className="text-slate-400 text-sm">Número Más Caliente</p>
              <p className="text-2xl font-bold text-pink-400 font-mono">
                {analysis?.hot_numbers?.[0]?.number?.toString().padStart(2, '0') || '--'}
              </p>
            </CardContent>
          </Card>
          <Card className="glass-card border-cyan-500/30">
            <CardContent className="py-4">
              <p className="text-slate-400 text-sm">Número Más Frío</p>
              <p className="text-2xl font-bold text-cyan-400 font-mono">
                {analysis?.cold_numbers?.[0]?.number?.toString().padStart(2, '0') || '--'}
              </p>
            </CardContent>
          </Card>
          <Card className="glass-card border-green-500/30">
            <CardContent className="py-4">
              <p className="text-slate-400 text-sm">Promedio Reciente</p>
              <p className="text-2xl font-bold text-green-400 font-mono">
                {analysis?.trends?.recent_average?.toFixed(1) || '--'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <FrequencyChart data={top20Numbers} lotteryColor={lottery.color} />
          <DecadeDistributionChart data={decadeDistribution} />
        </div>

        <TrendChart data={numberTrendData} resultsCount={results.length} />

        {/* Hot/Cold Numbers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card className="glass-card border-pink-500/30" data-testid="hot-numbers-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Flame size={20} className="text-pink-400" />
                Números Calientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {analysis?.hot_numbers?.slice(0, 10).map((item, idx) => (
                  <div key={`hot-${item.number}-${idx}`} className="flex flex-col items-center">
                    <div 
                      className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg"
                      style={{ 
                        backgroundColor: '#ec4899',
                        boxShadow: '0 0 15px rgba(236, 72, 153, 0.5)'
                      }}
                    >
                      {String(item.number).padStart(2, '0')}
                    </div>
                    <span className="text-slate-400 text-xs mt-1">{item.count}x</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-cyan-500/30" data-testid="cold-numbers-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Snowflake size={20} className="text-cyan-400" />
                Números Fríos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {analysis?.cold_numbers?.slice(0, 10).map((item, idx) => (
                  <div key={`cold-${item.number}-${idx}`} className="flex flex-col items-center">
                    <div 
                      className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg"
                      style={{ 
                        backgroundColor: '#06b6d4',
                        boxShadow: '0 0 15px rgba(6, 182, 212, 0.5)'
                      }}
                    >
                      {String(item.number).padStart(2, '0')}
                    </div>
                    <span className="text-slate-400 text-xs mt-1">{item.count}x</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Results Table */}
        <Card className="glass-card" data-testid="results-table">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar size={20} className="text-amber-400" />
              Últimos Resultados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left text-slate-400 py-3 px-4">Fecha</th>
                    <th className="text-left text-slate-400 py-3 px-4">Hora</th>
                    <th className="text-left text-slate-400 py-3 px-4">Números</th>
                  </tr>
                </thead>
                <tbody>
                  {results.slice(0, 20).map((result, idx) => (
                    <tr key={result.id || result.draw_date || `res-${idx}`} className="border-b border-slate-800/50">
                      <td className="py-3 px-4 text-white font-mono">{result.draw_date}</td>
                      <td className="py-3 px-4 text-slate-400">{result.draw_time}</td>
                      <td className="py-3 px-4">
                        <LotteryBallGroup numbers={result.numbers} size="sm" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      <DisclaimerBanner variant="fixed" />
    </div>
  );
};

export default LotteryHistory;
